import React, { createContext, useContext, useEffect, useReducer } from 'react'
import {
  getDefaultSettings,
  getDefaultVisibleKeyStats,
  getPersistedDisplaySettings,
  normalizeDisplaySettings,
  persistDisplaySettings
} from './components/helper'
import { type DisplaySettings, type ChartOrientation, type ReportChartType, type VisibleKeyStats } from './types'

export enum ReportsActionType {
  SET_SELECTED_TREND = 'SET_SELECTED_TREND',
  SET_IS_FULLSCREEN = 'SET_IS_FULLSCREEN',
  SET_DISPLAY_SETTINGS_CHANGED = 'SET_DISPLAY_SETTINGS_CHANGED',
  SET_DISPLAY_SETTINGS = 'SET_DISPLAY_SETTINGS',
  SET_CHART_ORIENTATION = 'SET_CHART_ORIENTATION',
  SET_SURVEY_VISIBLE_KEY_STATS = 'SET_SURVEY_VISIBLE_KEY_STATS',
  SET_GLOBAL_CHART_TYPE = 'SET_GLOBAL_CHART_TYPE',
  SET_QUESTION_CHART_TYPE = 'SET_QUESTION_CHART_TYPE',
  RESET_PRESENTATION_STATE = 'RESET_PRESENTATION_STATE',
}

interface ReportsState {
  selectedTrend: number
  isFullScreen: boolean
  displaySettingsChanged: boolean
  displaySettings: DisplaySettings
  chartOrientation: ChartOrientation
  surveyVisibleKeyStats: VisibleKeyStats
  globalChartType: ReportChartType
  questionChartOverrides: Record<string, ReportChartType>
}

type ReportsAction =
  { type: ReportsActionType.SET_SELECTED_TREND, payload: number } |
  { type: ReportsActionType.SET_IS_FULLSCREEN, payload: boolean } |
  { type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED, payload: boolean } |
  { type: ReportsActionType.SET_DISPLAY_SETTINGS, payload: DisplaySettings } |
  { type: ReportsActionType.SET_CHART_ORIENTATION, payload: ChartOrientation } |
  { type: ReportsActionType.SET_SURVEY_VISIBLE_KEY_STATS, payload: VisibleKeyStats } |
  { type: ReportsActionType.SET_GLOBAL_CHART_TYPE, payload: ReportChartType } |
  { type: ReportsActionType.SET_QUESTION_CHART_TYPE, payload: { questionId: string, chartType: ReportChartType } } |
  { type: ReportsActionType.RESET_PRESENTATION_STATE }

const initialState: ReportsState = {
  selectedTrend: 1,
  isFullScreen: true,
  displaySettingsChanged: false,
  displaySettings: getDefaultSettings(),
  chartOrientation: 'vertical',
  surveyVisibleKeyStats: getDefaultVisibleKeyStats(),
  globalChartType: 'distribution',
  questionChartOverrides: {}
}

const reportsReducer: React.Reducer<ReportsState, ReportsAction> = (state, action) => {
  switch (action.type) {
    case ReportsActionType.SET_SELECTED_TREND:
      return { ...state, selectedTrend: action.payload }
    case ReportsActionType.SET_IS_FULLSCREEN:
      return { ...state, isFullScreen: action.payload }
    case ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED:
      return { ...state, displaySettingsChanged: action.payload }
    case ReportsActionType.SET_DISPLAY_SETTINGS:
      return { ...state, displaySettings: normalizeDisplaySettings(action.payload) }
    case ReportsActionType.SET_CHART_ORIENTATION:
      return { ...state, chartOrientation: action.payload }
    case ReportsActionType.SET_SURVEY_VISIBLE_KEY_STATS:
      return { ...state, surveyVisibleKeyStats: action.payload }
    case ReportsActionType.SET_GLOBAL_CHART_TYPE:
      return {
        ...state,
        globalChartType: action.payload,
        questionChartOverrides: {}
      }
    case ReportsActionType.SET_QUESTION_CHART_TYPE:
      return {
        ...state,
        questionChartOverrides: {
          ...state.questionChartOverrides,
          [action.payload.questionId]: action.payload.chartType
        }
      }
    case ReportsActionType.RESET_PRESENTATION_STATE:
      return {
        ...state,
        chartOrientation: 'vertical',
        surveyVisibleKeyStats: getDefaultVisibleKeyStats(),
        globalChartType: 'distribution',
        questionChartOverrides: {}
      }
    default:
      return state
  }
}

const buildInitialReportsState = (): ReportsState => ({
  ...initialState,
  // Hydrate from local storage so a refresh restores the same report filters
  // and comparison labels the user last submitted.
  displaySettings: getPersistedDisplaySettings()
})

interface ReportsContextType extends ReportsState {
  dispatch: React.Dispatch<ReportsAction>
}

const ReportsContext = createContext<ReportsContextType | undefined>(undefined)

export const ReportsProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const [state, dispatch] = useReducer(reportsReducer, initialState, buildInitialReportsState)

  useEffect(() => {
    persistDisplaySettings(state.displaySettings)
  }, [state.displaySettings])

  return (
    <ReportsContext.Provider value={{ ...state, dispatch }}>
      {children}
    </ReportsContext.Provider>
  )
}

export const useReportsContext = (): ReportsContextType => {
  const context = useContext(ReportsContext)
  if (!context) throw new Error('useReportsContext must be used within ReportsProvider')
  return context
}
